import {useTranslation} from 'react-i18next';
import {useEffect, useRef, useState} from 'react';
import FaceDetectorApp from './FaceDetectorApp';
import {toast} from 'react-toastify';
import {PHOTO_PICKER_MODEL} from '../SubComponent/Models/Models';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param id: component id
 * @param useState setPhotoPicker: state to update the component model
 * @param boolean startup: to start the camera outside the component
 * @param var selectedCamera: variable that holds the selected camera
 * @param useState setSelectedCamera: state to update the camera selection
 * @param useState setStartup: setStartup state to set values when the option to start the camera is used outside the component
 * @param model typeSuggestions: required if showBtnCamera == true, the model must come from SUGGESTION_MODE_OPTION
 * @param boolean validateFace: default value is false, must be set to true if face validation is required
 * @param state setDisplayModal: State management to ensure only one modal is visible at a time.
 * @param state boolean reopenSuggestionShow: hHas a default value of true, and the variable is used to control whether the suggestions modal should be shown each time the photo capture process starts using the standalone camera component — that is, when only the camera functionality is used in an individual component.
 * @param event btnCancelStream :  is an event that is handled when the camera-only functionality is used in a separate component.
 */
const WebcamComponentApp = (
    {
        setPhotoPicker,
        startup=false,
        selectedCamera,
        setSelectedCamera,
        setStartup,
        validateFace = false,
        setDisplayModal=null,
        btnCancelStream=null,
        reopenSuggestionShow,
        isMultipleFile
    }
) => {


    const { t } = useTranslation();
    const webcamRef = useRef(null);
    const errorWebCanRef = useRef(false); // This variable prevents the toast from opening twice.
    const hasRequestedCameraRef = useRef(false); // This variable controls whether the camera permission dialog box is displayed only once.


    const [selectCameraDialogShow, setSelectCameraDialogShow] = useState(false);
    const [selfieSuggestionShow, setSelfieSuggestionShow] = useState(false);
    const [hasAgreedCameraUse, setHasAgreedCameraUse] = useState(false);

    const [countdown, setCountdown] = useState(3);
    const [capturing, setCapturing] = useState(false);
    const [initCount, setInitCount] = useState(false);

    const { handleValidateFace } = FaceDetectorApp();

    useEffect(() => {
        if(startup){
            hasRequestedCameraRef.current = false;
            handleStart();
        }
    }, [startup]);

    useEffect(() => {
        if(reopenSuggestionShow == false){
            getUserMedia();
        }
    }, [reopenSuggestionShow]);


    /**
     * useEffect for a countdown timer in photo capture, starting at 3 and ending at 0.
     */
    useEffect(() => {
        if (!initCount) return ;

        if (countdown == null || countdown <= 0) return;

        const timer = setTimeout(() => {
            if (countdown === 1) {
                captureSelfie();
            }
            setCountdown(prev => prev - 1);
            }, 1000);
        return () => clearTimeout(timer);
    }, [countdown, initCount]);

    /**
     * Function that starts the timer.
     */
    const startCountdown = () => {
        setPhotoPicker(prev => ({ ...prev, selfieSrc: null }))
        setCapturing(true);
        setCountdown(3);
    };

    /**
     * Function to take a photo and clear the fields.
     */
    const captureSelfie = async () => {
        if (webcamRef.current) {
            const screenshot = webcamRef.current.getScreenshot();
            if(validateFace){
                handleValidateFaces(screenshot);
            }else{
                setData(screenshot)
            }
        }
    };

    /**
     *  handler to validate faces
     * @param screenshot
     * @returns {Promise<void>}
     */
    const handleValidateFaces = async (screenshot) => {
        const faces = await handleValidateFace(screenshot);
        if(faces == 0){
            toast.warning(t("no_face_detected"))
            return;
        }else if(faces == 1){
            setData(screenshot)
        }else if(faces > 1) {
            toast.warning(t("more_than_one_face_detected"));
            return;
        }
    }

    /**
     * handle to set data
     * @param screenshot
     */
    const setData = (screenshot) => {
        if (isMultipleFile) {
            const newItem = {
                ...PHOTO_PICKER_MODEL,
                selfieSrc: screenshot
            };

            setPhotoPicker(prev => ({
                ...prev,
                listFiles: [...prev.listFiles, newItem]
            }));
        } else {
            setPhotoPicker(prev => ({ ...prev, selfieSrc: screenshot }))
        }
        setStartup(false);
        setCapturing(false);
        setCountdown(null);
        setInitCount(false)
    }

    /**
     * Function to clear the selfie src.
     */
    useEffect(() => {
        if (selectedCamera) {
            setPhotoPicker(prev => ({ ...prev, selfieSrc: null }))
        }
    }, [selectedCamera]);

    /**
     * Function to request permission to use the PC camera.
     * @returns {Promise<void>}
     */
    const getUserMedia = async () => {
        if (hasRequestedCameraRef.current) return;
        hasRequestedCameraRef.current = true;
        errorWebCanRef.current = false;

        if(!selfieSuggestionShow && reopenSuggestionShow){
            setSelfieSuggestionShow(true);
            if(setDisplayModal) setDisplayModal("d-none")
            return ;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            setHasAgreedCameraUse(true)
            stream.getTracks().forEach(track => track.stop());
            if(setDisplayModal)
                setDisplayModal("d-none")

            setSelectCameraDialogShow(true);
        } catch (error) {
            if (error.name === "NotAllowedError") {
                setStartup(false);
                if (!errorWebCanRef.current) {
                    errorWebCanRef.current = true;
                    camaraDenied();
                }
            }
        }
    };

    const camaraDenied = () => {
        navigator.permissions.query({ name: 'camera' }).then(({ state }) => {
            if (state === 'denied') {
                toast.warn(t("camera_access_is_blocked"));
                hasRequestedCameraRef.current = true;
                return;

            }
            if(state === 'prompt') {
                toast.warn(t("please_grant_permissions_to_use_the_camera"));
                hasRequestedCameraRef.current = true;
                return;
            }
        });
    }

    /**
     * Function to start the camera process.
     */
    const handleStart = () => {
        if(!selfieSuggestionShow && reopenSuggestionShow){
            setSelfieSuggestionShow(true);
            if(setDisplayModal) setDisplayModal("d-none")
            return ;
        }
        if (hasAgreedCameraUse) {
            if(setDisplayModal)
                setDisplayModal("d-none")

            setSelectCameraDialogShow(true);
        } else {
            getUserMedia();
        }
    };

    /**
     * Function to start the camera process.
     */
    const handleStartup = () => {
        setInitCount(true)
        startCountdown();
    };

    /**
     * Event to cancel the camera streaming
     */
    const cancelSelfie = () => {
        if(btnCancelStream != null){
            btnCancelStream()
        }else{
            setStartup(false)
            setSelectedCamera(null);
            setPhotoPicker(prev => ({ ...prev, selfieSrc: null }));
        }
    }

    return {
        t,
        webcamRef,
        selectCameraDialogShow,
        setSelectCameraDialogShow,
        selfieSuggestionShow,
        setSelfieSuggestionShow,
        hasAgreedCameraUse,
        setHasAgreedCameraUse,
        countdown,
        setCountdown,
        capturing,
        setCapturing,
        initCount,
        setInitCount,
        cancelSelfie,
        handleStartup,
        getUserMedia
    }

}

export default WebcamComponentApp;
